import { type TimelineEntry, TimelineStatus } from '~/app/pages/Transfers/_components/Timeline/types';
import { buildTimelineEntryForTransferReviews, buildTransferParty } from '~/app/pages/Transfers/_utils/timeline';
import { ROUTE } from '~/app/routes';
import { TransferStatus } from '~/lib/transfers';
import { getReturnReasonDisplay, wireReturnReasons } from '~/lib/transfers/returnReasons';
import type { Counterparty, WireTransfer } from '~/repositories';
import type { AccountNumber, BankAccount } from '~/repositories/BankAccountRepository';

const wireEvents = [
  {
    label: 'Created',
    field: 'createdAt',
  },
  {
    label: 'Pending Approval',
    field: 'preReviewAt',
  },
  {
    label: 'Initiated',
    field: 'initiatedAt',
  },
  {
    label: 'Pending Submission',
    field: 'pendingSubmissionAt',
    status: TimelineStatus.Pending,
  },
  {
    label: 'Submitted',
    field: 'submittedAt',
  },
  {
    label: 'Completed',
    field: 'completedAt',
    status: TimelineStatus.Success,
  },
  {
    label: 'Rejected',
    field: 'rejectedAt',
    status: TimelineStatus.Danger,
  },
];

export const buildWireEvents = ({
  transfer,
  account,
  counterparty,
}: {
  transfer: WireTransfer;
  account: {
    accountNumber: AccountNumber;
    bankAccount: BankAccount;
    entityType?: 'person' | 'business';
  };
  counterparty: Counterparty;
}): TimelineEntry[] => {
  const rejected = (transfer.status.toLowerCase() as TransferStatus) === TransferStatus.REJECTED;
  const events = wireEvents
    .filter(
      (event) =>
        event.field === 'createdAt' ||
        (!rejected && event.field === 'completedAt') ||
        !!transfer[event.field as keyof typeof transfer]
    )
    .map((event) => {
      if (event.field === 'preReviewAt') {
        return buildTimelineEntryForTransferReviews(
          transfer[event.field as keyof typeof transfer] as string | undefined,
          transfer.reviews
        );
      }

      let amount;
      if (transfer.amount) {
        amount = {
          value: typeof transfer.amount === 'string' ? parseFloat(transfer.amount) : transfer.amount,
          isAdding: event.field === 'completedAt',
          currencyCode: 'USD',
        };
      }

      const date = (transfer[event.field as keyof typeof transfer] as string | undefined)
        ? new Date(transfer[event.field as keyof typeof transfer] as string)
        : undefined;

      const partyData =
        event.field === 'createdAt' || event.field === 'completedAt'
          ? buildTransferParty({
              account,
              counterparty,
              isIncoming: transfer.isIncoming,
              isOriginator: event.field === 'createdAt',
            })
          : undefined;

      const defaultStatus = rejected ? TimelineStatus.Danger : TimelineStatus.Pending;
      const status = !!transfer[event.field as keyof typeof transfer] ? event.status : defaultStatus;

      return {
        label: event.label,
        amount,
        date,
        partyData,
        status,
      };
    });

  if (rejected) {
    events.push({
      label: 'Rejected',
      amount: transfer.amount
        ? {
            value: typeof transfer.amount === 'string' ? parseFloat(transfer.amount) : transfer.amount,
            isAdding: false,
            currencyCode: 'USD',
          }
        : undefined,
      status: TimelineStatus.Danger,
      partyData: buildTransferParty({
        account,
        counterparty,
        isIncoming: transfer.isIncoming,
        isOriginator: false,
      }),
    });
  }

  return events;
};

// The return of an incoming wire is its own outgoing transfer, linked by reversalPairTransferId.
// The generated WireTransfer type predates return_reason, hence the widening.
export const buildWireReturnEvents = (
  returnTransfer: WireTransfer & { returnReason?: string | null }
): TimelineEntry[] => {
  const amount = typeof returnTransfer.amount === 'string' ? parseFloat(returnTransfer.amount) : returnTransfer.amount;
  const link = { label: 'View return', path: `${ROUTE.TRANSFERS}/edit/wire/${returnTransfer.id}` };
  const reason = returnTransfer.returnReason
    ? getReturnReasonDisplay(wireReturnReasons, returnTransfer.returnReason)
    : undefined;
  const events: TimelineEntry[] = [
    {
      label: 'Return Initiated',
      date: new Date(returnTransfer.createdAt),
      status: TimelineStatus.Returned,
      link,
      returnData: {
        code: '',
        reason: reason?.code ?? '',
        reasonDescription: reason?.description,
        description: returnTransfer.description ?? '',
        amount,
        currencyCode: returnTransfer.currencyCode || 'USD',
      },
    },
  ];

  if (returnTransfer.rejectedAt) {
    events.push({
      label: 'Return Rejected',
      date: new Date(returnTransfer.rejectedAt),
      status: TimelineStatus.Danger,
      link,
    });
  } else {
    events.push({
      label: 'Returned',
      date: returnTransfer.completedAt ? new Date(returnTransfer.completedAt) : undefined,
      status: returnTransfer.completedAt ? TimelineStatus.Success : TimelineStatus.Pending,
    });
  }

  return events;
};

// Mirrors the backend's reversal checks: only a completed or held incoming wire can be returned, and a
// linked return blocks another unless it was rejected. The status union predates HOLD, hence the widening.
export const isWireReturnable = (transfer: WireTransfer, returnTransfer?: WireTransfer) =>
  transfer.isIncoming &&
  ['COMPLETED', 'HOLD'].includes(transfer.status as string) &&
  (!transfer.reversalPairTransferId || returnTransfer?.status === 'REJECTED');
